<template>
    <b-modal
        id="modal-generate-integration-key"
        shadow
        bg-variant="white"
        backdrop
        hide-footer
        centered
        no-enforce-focus
        @hidden="onHidden"
    >
        <template #modal-header>
            <div class="d-flex align-items-center justify-content-start w-100 title-modal">
                <span class="modal-title">{{ $t("IntegrationKeys.CreateTitle") }}</span>
            </div>
            <button
                type="button"
                aria-label="Close"
                class="close"
                @click="closeModal"
            >
                <CloseModalIcon />
            </button>
        </template>

        <div class="modal-integration-key__container">
            <div
                v-if="step === 'form'"
                class="modal-generate-step"
            >
                <b-form-group
                    label-for="integration-key-description"
                    :label="$t('IntegrationKeys.Description')"
                    :invalid-feedback="generateDescriptionInvalidFeedback"
                >
                    <b-form-input
                        id="integration-key-description"
                        v-model.trim="description"
                        :state="descriptionState"
                        :placeholder="$t('TypeHere')"
                        maxlength="250"
                    />
                </b-form-group>
                <div class="modal-integration-key__footer d-flex justify-content-end flex-wrap gap-1">
                    <sys-button
                        scheme="square"
                        variant="outline"
                        size="md"
                        :disabled="sendingForm"
                        @click="closeModal"
                    >
                        {{ $t("IntegrationKeys.Cancel") }}
                    </sys-button>
                    <sys-button
                        v-if="!sendingForm"
                        scheme="square"
                        variant="contained"
                        size="md"
                        @click="submitGenerate"
                    >
                        {{ $t("IntegrationKeys.GenerateSubmit") }}
                    </sys-button>
                </div>
            </div>

            <div
                v-else
                class="modal-generate-step"
            >
                <b-alert
                    show
                    variant="warning"
                    class="mb-2 ik-generate-alert"
                >
                    {{ $t("IntegrationKeys.KeyWarning") }}
                </b-alert>
                <div class="integration-key-value-wrap">
                    <input
                        ref="generatedKeyInput"
                        type="text"
                        readonly
                        tabindex="0"
                        spellcheck="false"
                        autocomplete="off"
                        class="integration-key-field"
                        :value="generatedKey"
                        @click="$event.target.select()"
                        @focus="$event.target.select()"
                    />
                </div>
                <div class="modal-integration-key__footer d-flex justify-content-end flex-wrap gap-1">
                    <sys-button
                        scheme="square"
                        variant="contained"
                        size="md"
                        @click="copyKey"
                    >
                        {{ $t("IntegrationKeys.CopyKey") }}
                    </sys-button>
                    <sys-button
                        scheme="square"
                        variant="outline"
                        size="md"
                        @click="closeModal"
                    >
                        {{ $t("IntegrationKeys.CloseConfirm") }}
                    </sys-button>
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script>
    import { BModal, BFormGroup, BFormInput, BAlert } from "bootstrap-vue";
    import CloseModalIcon from "@/@core/assets/icons/close-modal-icon.svg";
    import SysButton from "@/@core/components/sys-button";
    import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
    import { mapGetters, mapMutations } from "vuex";

    export default {
        name: "ModalGenerateIntegrationKey",
        components: {
            BModal,
            BFormGroup,
            BFormInput,
            BAlert,
            CloseModalIcon,
            SysButton
        },
        data() {
            return {
                descriptionMaxLength: 250,
                step: "form",
                description: "",
                descriptionState: null,
                sendingForm: false,
                generatedKey: ""
            };
        },
        computed: {
            ...mapGetters("auth", ["getAccountId"]),
            generateDescriptionInvalidFeedback() {
                const d = this.description || "";
                if (!d.trim()) {
                    return this.$t("IntegrationKeys.RequiredDescription");
                }
                if (d.length > this.descriptionMaxLength) {
                    return this.$t("IntegrationKeys.DescriptionMaxLength");
                }
                return this.$t("IntegrationKeys.RequiredDescription");
            }
        },
        methods: {
            ...mapMutations("app", ["UPDATE_FLAG_SHOW_OVERLAY"]),
            closeModal() {
                this.$bvModal.hide("modal-generate-integration-key");
            },
            onHidden() {
                this.step = "form";
                this.description = "";
                this.descriptionState = null;
                this.generatedKey = "";
                this.sendingForm = false;
            },
            showToast(title, icon, text, variant) {
                this.$toast({
                    component: ToastificationContent,
                    props: { title, icon, text, variant }
                });
            },
            showCopySuccessToast() {
                this.showToast(
                    this.$t("IntegrationKeys.Success"),
                    "SmileIcon",
                    this.$t("IntegrationKeys.Copied"),
                    "success"
                );
            },
            showCopyErrorToast() {
                this.showToast(
                    this.$t("IntegrationKeys.Error"),
                    "FrownIcon",
                    this.$t("IntegrationKeys.CopyFailed"),
                    "danger"
                );
            },
            copyViaHiddenTextareaSync(value) {
                const text = String(value || "");
                if (!text) {
                    throw new Error("empty key");
                }
                const ta = document.createElement("textarea");
                ta.value = text;
                ta.setAttribute("readonly", "readonly");
                ta.setAttribute("aria-hidden", "true");
                ta.style.cssText =
                    "position:fixed;left:-9999px;top:0;width:1px;height:1px;padding:0;border:none;outline:none;opacity:0;";
                document.body.appendChild(ta);
                ta.focus({ preventScroll: true });
                ta.select();
                ta.setSelectionRange(0, text.length);
                const ok = document.execCommand("copy");
                document.body.removeChild(ta);
                if (!ok) {
                    throw new Error("execCommand copy returned false");
                }
            },
            copyKey() {
                const key = String(this.generatedKey || "").trim();
                if (!key) {
                    this.showCopyErrorToast();
                    return;
                }

                const input = this.$refs.generatedKeyInput;
                if (input) {
                    input.focus({ preventScroll: true });
                    input.select();
                    if (typeof input.setSelectionRange === "function") {
                        input.setSelectionRange(0, key.length);
                    }
                    if (document.execCommand("copy")) {
                        this.showCopySuccessToast();
                        return;
                    }
                }

                const clip = navigator.clipboard;
                if (clip && typeof clip.writeText === "function") {
                    clip.writeText(key).then(
                        () => this.showCopySuccessToast(),
                        () => {
                            try {
                                this.copyViaHiddenTextareaSync(key);
                                this.showCopySuccessToast();
                            } catch (e) {
                                this.showCopyErrorToast();
                            }
                        }
                    );
                } else {
                    try {
                        this.copyViaHiddenTextareaSync(key);
                        this.showCopySuccessToast();
                    } catch (e) {
                        this.showCopyErrorToast();
                    }
                }
            },
            async submitGenerate() {
                const d = this.description || "";
                const ok =
                    !!d.trim() && d.length <= this.descriptionMaxLength;
                this.descriptionState = ok;
                if (!ok) return;

                const accountId = this.getAccountId;
                if (accountId == null) {
                    this.showToast(
                        this.$t("IntegrationKeys.Error"),
                        "FrownIcon",
                        this.$t("IntegrationKeys.MissingAccount"),
                        "danger"
                    );
                    return;
                }

                this.UPDATE_FLAG_SHOW_OVERLAY(true);
                this.sendingForm = true;
                try {
                    const response = await this.$httpMyConnect.post(
                        "/api/integration-agents/generate-key",
                        { description: this.description },
                        { params: { accountId } }
                    );

                    const body = response.data;
                    if (body.success === false) {
                        this.showToast(
                            this.$t("IntegrationKeys.Error"),
                            "FrownIcon",
                            body.message || this.$t("IntegrationKeys.LoadError"),
                            "danger"
                        );
                        return;
                    }

                    this.generatedKey = body.data ?? "";
                    this.step = "result";
                    this.$emit("generated");
                } catch (error) {
                    const msg =
                        error.response?.data?.message ||
                        error.message ||
                        this.$t("IntegrationKeys.LoadError");
                    this.showToast(this.$t("IntegrationKeys.Error"), "FrownIcon", msg, "danger");
                } finally {
                    this.sendingForm = false;
                    this.UPDATE_FLAG_SHOW_OVERLAY(false);
                }
            }
        }
    };
</script>

<style lang="scss">
    #modal-generate-integration-key {
        .modal-dialog {
            width: 95%;
            max-width: 560px;
        }

        .modal-content {
            width: 100%;
            right: 0 !important;
            border-radius: 6px;
            overflow: hidden;
            border: 1px solid #e0e0e0;
            box-shadow: 0px 8px 32px rgba(58, 46, 38, 0.12);
        }

        .modal-header {
            position: relative !important;
            display: flex;
            align-items: center;
            height: 66px;
            background-color: #eceff1;
            padding-top: 0;
            padding-bottom: 0;
            border-bottom: 1px solid #e0e0e0;

            .title-modal {
                height: 100%;
            }

            .modal-title {
                color: #4c4541;
                font-size: 18px;
                font-weight: 600;
                line-height: 26px;
            }

            .close {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 28px;
                height: 28px;
                min-width: 28px;
                min-height: 28px;
                max-width: 28px;
                max-height: 28px;
                padding: 0;
                margin: 0;
                opacity: 1;
                transform: translate(6px, 5px);
                border: 1px solid #974900 !important;
                border-radius: 5px;
                background: transparent !important;
                box-shadow: none !important;

                svg {
                    min-height: 12px;
                    min-width: 12px;
                    max-width: 12px;
                    max-height: 12px;
                    fill: #974900 !important;
                }

                svg path {
                    fill: #974900 !important;
                }

                &:hover {
                    opacity: 1;
                    transform: translate(6px, 5px);
                    background-color: #ffede2 !important;
                }

                &:active {
                    background-color: #ffdbc4 !important;
                }

                &:focus {
                    outline: none;
                    box-shadow: 0 0 0 0.15rem rgba(151, 73, 0, 0.25);
                }
            }
        }

        .modal-body {
            padding: 0 !important;
        }

        .modal-integration-key__container {
            display: flex;
            flex-direction: column;
            padding: 20px 16px;
            gap: 20px;
            background-color: #fff;

            .modal-generate-step {
                display: flex;
                flex-direction: column;
                gap: 20px;
            }

            .form-group {
                margin: 0;
            }

            label {
                font-weight: 400;
                font-size: 12px;
                line-height: 16px;
                color: #4c4541;
                padding-bottom: 0 !important;
                margin-bottom: 4px;
            }

            .form-control {
                color: #4c4541;
                border-color: #d5c8ba;
                border-radius: 6px;
                font-size: 14px;
                line-height: 20px;

                &::placeholder {
                    color: #cfc4be;
                }

                &:focus {
                    border-color: #974900;
                    box-shadow: none;
                }
            }

            .form-control.is-invalid {
                background-image: none !important;
                border-color: #dc3545;
            }

            .invalid-feedback {
                margin-left: 5px !important;
                font-size: 10px;
            }

            .modal-integration-key__footer {
                display: flex;
                justify-content: flex-end;
                flex-wrap: wrap;
                margin-top: 0;
                gap: 8px;
            }

            .ik-generate-alert {
                background-color: #fff8f0;
                border-color: rgba(151, 73, 0, 0.35);
                color: #4c4541;
                border-radius: 6px;
            }

            .integration-key-value-wrap {
                padding: 12px;
                background: #f5f5f5;
                border: 1px solid #e8e4e0;
                border-radius: 6px;
                word-break: break-all;
            }

            .integration-key-field {
                display: block;
                width: 100%;
                margin: 0;
                padding: 0;
                border: none;
                outline: none;
                box-shadow: none;
                background: transparent;
                font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
                    monospace;
                font-size: 13px;
                line-height: 1.45;
                color: #4c4541;
                cursor: text;

                &:focus {
                    outline: none;
                    box-shadow: none;
                }
            }
        }
    }
</style>
